import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import type { Invoice, InvoiceLinks } from '../../api/types.gen'
import { formatDate, formatSoles } from './types'
import { ApiException, api } from '../../api/client'
import { Banner, Button, Dialog, Skeleton } from '../../ui'

/** Cómo se llama cada documento, tal como sale en el papel. */
const DENOMINACION: Record<Invoice['document_type'], string> = {
  factura: 'Factura electrónica',
  boleta: 'Boleta de venta electrónica',
  nota_credito: 'Nota de crédito electrónica',
}

/**
 * El comprobante que el estudio abre desde su historial de cobros.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * Lo emite y lo guarda ChambaTotal (#166)
 * ─────────────────────────────────────────────────────────────────────────────
 * Aquí se enseña lo que contestó —el número, si es de pruebas, los importes— y
 * los dos archivos se abren allí: el XML firmado, que ES la factura y lo que
 * pide el contador, y el PDF que la representa. Sus enlaces caducan, así que se
 * piden al pulsar y no antes.
 */
export function InvoiceDialog({
  invoiceId,
  onClose,
}: {
  invoiceId: string | null
  onClose: () => void
}) {
  const [abriendo, setAbriendo] = useState<'pdf' | 'xml' | null>(null)
  const [fallo, setFallo] = useState<string | null>(null)

  const comprobante = useQuery({
    queryKey: ['billing', 'invoice', invoiceId],
    queryFn: () => api.get<{ data: Invoice }>(`/billing/invoices/${invoiceId ?? ''}`),
    enabled: invoiceId !== null,
  })

  const abrir = async (cual: 'pdf' | 'xml') => {
    if (invoiceId === null) return

    setFallo(null)
    setAbriendo(cual)

    /*
     | La pestaña se abre YA, en el clic, y la dirección se le pone cuando llega.
     | Abrirla después de esperar a la red la convierte en una ventana emergente,
     | y el navegador la bloquea sin decir nada.
     */
    const ventana = window.open('', '_blank')

    try {
      const enlaces = await api.get<{ data: InvoiceLinks }>(`/billing/invoices/${invoiceId}/links`)
      const url = cual === 'pdf' ? enlaces.data.pdf_url : enlaces.data.xml_url

      if (url === null) {
        throw new Error('Ese archivo todavía no está disponible. Inténtalo en unos minutos.')
      }

      if (ventana === null) {
        window.location.assign(url)
      } else {
        // La pestaña nueva no puede tocar el panel: es de otro sitio.
        ventana.opener = null
        ventana.location.href = url
      }
    } catch (error) {
      ventana?.close()
      setFallo(
        error instanceof ApiException || error instanceof Error
          ? error.message
          : 'No se pudo abrir el comprobante. Inténtalo de nuevo.',
      )
    } finally {
      setAbriendo(null)
    }
  }

  const datos = comprobante.data?.data
  const aceptado = datos?.sunat_status === 'accepted'

  return (
    <Dialog
      open={invoiceId !== null}
      onClose={onClose}
      title={datos === undefined ? 'Comprobante' : DENOMINACION[datos.document_type]}
      description={datos?.full_number ?? undefined}
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
          <Button
            variant="secondary"
            disabled={!aceptado || abriendo !== null}
            busy={abriendo === 'xml' && 'Abriendo…'}
            onClick={() => {
              void abrir('xml')
            }}
          >
            XML firmado
          </Button>
          <Button
            disabled={!aceptado || abriendo !== null}
            busy={abriendo === 'pdf' && 'Abriendo…'}
            onClick={() => {
              void abrir('pdf')
            }}
          >
            Ver en PDF
          </Button>
        </div>
      }
    >
      {comprobante.isPending ? (
        <Skeleton className="h-48 w-full rounded-card" />
      ) : comprobante.error !== null || datos === undefined ? (
        <p className="text-body text-ink-muted">No se pudo cargar el comprobante.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Arriba, y antes de leer nada: uno de pruebas no vale ante SUNAT, y
              enterarse después de haberlo contabilizado llega tarde. */}
          {datos.environment === 'beta' && (
            <Banner tone="danger" title="Comprobante de prueba" live="alert">
              No existe ante SUNAT y no sirve para tu contabilidad. Escríbenos y te enviamos el de
              verdad.
            </Banner>
          )}

          {!aceptado && (
            <Banner tone="warning" title="Todavía no está emitido">
              SUNAT aún no lo ha confirmado. En cuanto lo haga, lo podrás abrir desde aquí.
            </Banner>
          )}

          <dl className="flex flex-col gap-1 text-body">
            <Fila etiqueta="Cliente" valor={datos.customer_legal_name ?? '—'} />
            {datos.customer_doc_type !== null && (
              <Fila etiqueta={datos.customer_doc_type} valor={datos.customer_doc_number ?? '—'} />
            )}
            {datos.issued_at !== null && <Fila etiqueta="Emitido" valor={formatDate(datos.issued_at)} />}
          </dl>

          <dl className="flex flex-col gap-1 border-t border-line pt-3">
            <Fila etiqueta="Op. gravada" valor={formatSoles(datos.subtotal_cents)} />
            <Fila etiqueta="IGV" valor={formatSoles(datos.tax_cents)} />
            <Fila etiqueta="Importe total" valor={formatSoles(datos.total_cents)} fuerte />
          </dl>

          <p className="text-caption text-ink-subtle">
            El XML firmado es el comprobante que vale ante SUNAT, y el que te pedirá tu contador. El
            PDF lo representa.
          </p>

          {fallo !== null && (
            <Banner tone="danger" title="No se pudo abrir" live="alert">
              {fallo}
            </Banner>
          )}
        </div>
      )}
    </Dialog>
  )
}

function Fila({
  etiqueta,
  valor,
  fuerte = false,
}: {
  etiqueta: string
  valor: string
  fuerte?: boolean
}) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-ink-muted">{etiqueta}</dt>
      <dd
        className={fuerte ? 'font-semibold tabular-nums text-ink' : 'tabular-nums text-ink'}
        data-numeric
      >
        {valor}
      </dd>
    </div>
  )
}
